import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { formatMoney, formatNumber } from '@awj/i18n';
import { Skeleton } from '@awj/ui';

import { EmptyState } from '~/components/EmptyState.tsx';
import { ExportMenu } from '~/components/ExportMenu.tsx';
import { Icon } from '~/components/Icon.tsx';
import { Select } from '~/components/Select.tsx';
import { PageTitle } from '~/components/PageTitle.tsx';
import { Pager } from '~/components/Pager.tsx';
import { useI18n } from '~/i18n/index.tsx';
import { allProducts, useCategories, useProducts } from '~/lib/catalog-queries.ts';
import { productColumns } from '~/lib/export-columns.ts';
import { useRowLink } from '~/lib/row-link.ts';
import { mediaUrl } from '~/lib/media.ts';

const STATUSES = ['Draft', 'Active', 'Archived'] as const;
const SORTS = ['newest', 'name', 'price-asc', 'price-desc', 'stock'] as const;

/**
 * Every product, filtered.
 *
 * State lives in the URL exactly as it does on the shop's own listing: a member of staff who
 * finds the twelve products that need restocking can send that link to a colleague, and coming
 * back through the browser's history returns to the same view rather than to page one.
 */
export function ProductsPage() {
  const { t, locale } = useI18n();
  const [params, setParams] = useSearchParams();

  const query = {
    search: params.get('q') ?? '',
    category: params.get('category') ?? '',
    status: params.get('status') ?? '',
    sort: params.get('sort') ?? '',
    page: Number(params.get('page')) || 1,
  };

  const products = useProducts(query);
  const categories = useCategories();
  const rowLink = useRowLink();

  // The search box types faster than the server answers; the URL follows a beat behind.
  const [search, setSearch] = useState(query.search);
  useEffect(() => setSearch(query.search), [query.search]);

  useEffect(() => {
    if (search === query.search) {
      return;
    }

    const timer = setTimeout(() => update({ q: search, page: '' }), 350);

    return () => clearTimeout(timer);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  function update(changes: Record<string, string>) {
    const next = new URLSearchParams(params);

    for (const [key, value] of Object.entries(changes)) {
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
    }

    // Any change but the page itself starts again at the first page — a filter applied on page
    // four otherwise lands on a page four that may no longer exist.
    if (!('page' in changes)) {
      next.delete('page');
    }

    setParams(next, { replace: true });
  }

  return (
    <>
      <PageTitle
        title={t('nav.products')}
        actions={
          <Link className="awj-button awj-button--primary awj-button--sm" to="/products/new">
            <Icon name="plus" size={17} />
            {t('catalog.newProduct')}
          </Link>
        }
      />

      <div className="filter-bar">
        <div className="filter-bar__row">
          <label className="filter-bar__search">
            <span className="sr-only">{t('catalog.searchProducts')}</span>
            <span className="filter-bar__search-icon">
              <Icon name="search" size={16} />
            </span>
            <input
              className="filter-bar__search-input"
              type="search"
              value={search}
              placeholder={t('catalog.searchProducts')}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>

          <div className="filter-bar__controls">
            <div className="filter-bar__field">
              <label htmlFor="filter-category">{t('catalog.category')}</label>
              <Select
                id="filter-category"
                value={query.category}
                onChange={(category) => update({ category })}
                placeholder={t('catalog.allCategories')}
                options={(categories.data ?? []).map((category) => ({
                  value: category.slugEn,
                  label: locale === 'ar' ? category.nameAr : category.nameEn,
                }))}
              />
            </div>

            <div className="filter-bar__field">
              <label htmlFor="filter-status">{t('catalog.status')}</label>
              <Select
                id="filter-status"
                value={query.status}
                onChange={(status) => update({ status })}
                placeholder={t('catalog.allStatuses')}
                options={STATUSES.map((status) => ({
                  value: status,
                  label: t(`productStatus.${status}` as 'productStatus.Active'),
                }))}
              />
            </div>

            <div className="filter-bar__field">
              <label htmlFor="filter-sort">{t('catalog.sort')}</label>
              <Select
                id="filter-sort"
                value={query.sort}
                onChange={(sort) => update({ sort })}
                options={SORTS.map((sort) => ({
                  value: sort === 'newest' ? '' : sort,
                  label: t(`catalogSort.${sort}` as 'catalogSort.newest'),
                }))}
              />
            </div>
          </div>

          <div className="filter-bar__actions">
            <ExportMenu
              file="products"
              sheet={t('nav.products')}
              columns={productColumns(t, locale)}
              rows={() => allProducts(query)}
              hint={t('export.allMatching')}
              disabled={!products.data?.total}
            />
          </div>
        </div>
      </div>

      {products.isPending ? (
        <Skeleton height="320px" />
      ) : products.isError ? (
        <EmptyState
          icon="alert"
          title={t('common.somethingWentWrong')}
          hint={t('common.somethingWentWrongHint')}
          action={
            <button type="button" className="awj-button awj-button--secondary" onClick={() => products.refetch()}>
              {t('common.retry')}
            </button>
          }
        />
      ) : products.data.items.length === 0 ? (
        <EmptyState icon="box" title={t('catalog.noProducts')} hint={t('catalog.noProductsHint')} tall />
      ) : (
        <div className="data-table">
            <div className="data-table__scroll" tabIndex={0} role="region" aria-label={t('nav.products')}>
              <table className="data-table__table">
                <thead>
                  <tr>
                    <th className="data-table__th" scope="col">{t('catalog.product')}</th>
                    <th className="data-table__th" scope="col">{t('catalog.category')}</th>
                    <th className="data-table__th" scope="col">{t('catalog.status')}</th>
                    <th scope="col" className="data-table__th data-table__cell--end">
                      {t('catalog.stock')}
                    </th>
                    <th scope="col" className="data-table__th data-table__cell--end">
                      {t('catalog.price')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {products.data.items.map((product) => {
                    const image = mediaUrl(product.primaryImageUrl);
                    const name = locale === 'ar' ? product.nameAr : product.nameEn;

                    return (
                      <tr key={product.id} {...rowLink(`/products/${product.id}`)}>
                        <td className="data-table__td">
                          <span className="cell-identity">
                            <span className="thumb">
                              {image ? (
                                <img src={image} alt="" width={46} height={46} loading="lazy" />
                              ) : (
                                <span aria-hidden="true">{name.trim().charAt(0)}</span>
                              )}
                            </span>
                            <span className="cell-identity__text">
                              {/* The whole row's subject; the link belongs on the name. */}
                              <Link className="data-table__link" to={`/products/${product.id}`}>
                                <bdi>{name}</bdi>
                              </Link>
                              <span className="data-table__sub mono">{product.sku}</span>
                            </span>
                          </span>
                        </td>
                        <td className="data-table__td">
                          <bdi>{locale === 'ar' ? product.categoryNameAr : product.categoryNameEn}</bdi>
                        </td>
                        <td className="data-table__td">
                          <span className={`status status--${product.status.toLowerCase()}`}>
                            {t(`productStatus.${product.status}` as 'productStatus.Active')}
                          </span>
                          {product.isFeatured ? (
                            <span className="chip chip--accent">{t('catalog.featured')}</span>
                          ) : null}
                        </td>
                        <td className="data-table__td data-table__cell--end">
                          {/*
                            Sold out is not "low", and the API's low-stock flag deliberately excludes
                            it — so a nought would otherwise sit in this column saying nothing. It is
                            the most urgent row in a restocking list; it is marked as such.
                          */}
                          <bdi className={product.availableStock === 0 ? 'is-out' : product.isLowStock ? 'is-low' : undefined}>
                            {formatNumber(locale, product.availableStock)}
                          </bdi>
                          {product.availableStock === 0 ? (
                            <span className="data-table__sub is-out">{t('catalog.outOfStock')}</span>
                          ) : product.isLowStock ? (
                            <span className="data-table__sub is-low">{t('catalog.lowStock')}</span>
                          ) : null}
                        </td>
                        <td className="data-table__td data-table__cell--end">
                          <bdi>{formatMoney(locale, product.price)}</bdi>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Pager
              page={products.data.page}
              totalPages={products.data.totalPages ?? 1}
              total={products.data.total}
              onPage={(page) => update({ page: String(page) })}
            />
        </div>
      )}
    </>
  );
}
